/**
 * SEO 링크 태그(canonical/hreflang)용 공통 헬퍼.
 *
 * origin 은 배포 환경변수(REACT_APP_DOMAIN)로 고정한다 — window.location.origin 을 쓰면
 * 프리뷰/미러/비정규 도메인에서 그 도메인이 canonical/hreflang 에 그대로 박혀
 * 잘못된 정본 신호를 보내게 된다. (prod=checkmatekorea.com, dev=dev.checkmatekorea.com,
 * 엣지 og-injector 의 SITE_ORIGIN 과 동일 규칙)
 */
import { UNIVERSITY_LANDING_SLUGS } from './universitySlugs';

// 지원 언어 (en=접두사 없음 = x-default). src/i18n.js supportedLngs 와 일치.
export const SEO_LANGS = ['en', 'vi', 'zh', 'ja'] as const;

export const seoOrigin = (): string => {
  const domain = process.env.REACT_APP_DOMAIN;
  if (domain) return `https://${domain}`;
  // 로컬 개발 등 env 미주입 시 폴백 (SEO 크롤러가 보는 환경 아님)
  return typeof window !== 'undefined' ? window.location.origin : '';
};

// 색인 대상 공개 페이지 — sitemap-app.xml·엣지 og-injector STATIC_PAGE_KEYS 와 반드시 동일 집합.
// 이 목록 밖의 라우트(마이페이지·카트·결제 등)는 noindex 대상이라
// canonical/hreflang 을 내보내지 않는다 — noindex 페이지에 hreflang 이 붙으면
// 서치콘솔에 "대체 페이지가 noindex" 경고가 쌓인다.
// checkmate-mall 제외: 본문이 외부 iframe(imweb.me) 래퍼라 자체 색인 콘텐츠가 없어 SEO 타깃 아님
// (SeoMetaTags 로 탭 제목만 제공, sitemap 에도 미포함).
// (export 는 universitySlugs.sync.test.ts 의 엣지 STATIC_PAGE_KEYS 동기화 검사용)
export const INDEXABLE_EXACT = new Set([
  '/',
  '/listing',
  '/guide',
  '/korean-housing-info',
  '/request-room',
  '/all-services',
  '/membership',
  '/reviews',
  '/about-us',
  '/jobs',
]);

// 매물 상세 (/listing/:id)
const LISTING_DETAIL_PATTERN = /^\/listing\/[^/]+$/;

// 대학 랜딩 (/university/:slug) — 등록된 슬러그만 색인 대상 (미등록 슬러그는 404 리다이렉트)
const UNIVERSITY_LANDING_PATTERN = /^\/university\/([a-z0-9-]+)$/;

/** 트레일링 슬래시 제거 ('/listing/' → '/listing'). 루트('/')는 그대로. */
export const normalizeBasePath = (basePath: string): string =>
  basePath.length > 1 && basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;

export const isIndexablePath = (basePath: string): boolean => {
  const normalized = normalizeBasePath(basePath);
  if (INDEXABLE_EXACT.has(normalized) || LISTING_DETAIL_PATTERN.test(normalized)) return true;
  const univMatch = normalized.match(UNIVERSITY_LANDING_PATTERN);
  // hasOwnProperty: 'constructor' 등 프로토타입 키 오탐 방지 ('in' 연산자는 체인까지 본다)
  return univMatch ? Object.prototype.hasOwnProperty.call(UNIVERSITY_LANDING_SLUGS, univMatch[1]) : false;
};

/**
 * 언어별 정본 URL. en 은 접두사 없음, 나머지는 /{lang} 접두사.
 * 루트('/')일 때 /vi/ 같은 군더더기 슬래시 방지.
 * (Hreflang·canonical·엣지 og-injector·sitemap-app.xml 공통 규칙)
 */
export const langHref = (lang: string, basePath: string): string => {
  const path = normalizeBasePath(basePath);
  return lang === 'en' ? `${seoOrigin()}${path}` : `${seoOrigin()}/${lang}${path === '/' ? '' : path}`;
};

/**
 * 현재 pathname 의 언어 접두사 (없으면 'en').
 * getPathWithoutLang(converter.ts) 와 동일하게 대소문자 무관 + 소문자 정규화 —
 * 둘이 어긋나면 canonical 언어와 basePath 언어가 불일치할 수 있어 규칙을 맞춘다.
 */
export const langFromPathname = (pathname: string): string => {
  const match = pathname.match(/^\/(vi|zh|ja)(?=\/|$)/i);
  return match ? match[1].toLowerCase() : 'en';
};
